import { ReactNode } from "react";
import styled from "styled-components";

// PrimeFlex carries the layout; only the brand colours, the exact type scale and
// the two filters below need real CSS, so they ride along as inline styles.
// The screen gutter and the card chrome are the exception -- they need a media
// query to go full-bleed on a phone, which an inline style cannot express.
// Their values are taken from the Auth0 sign-in page (spa/auth0/login.html) so
// the two screens read as one flow; change them together.
const EYEBROW_CLASS = "text-xs font-semibold uppercase";
const EYEBROW_STYLE = { letterSpacing: "0.14em" };

// The mark is a single flat colour, so this repaints it white for the purple
// panel rather than carrying a second asset that has to be kept in sync. The
// width is .ch-brand__logo from login.html verbatim, so the mark reads the same
// size on both auth screens at every viewport, not just at one.
const WHITE_LOGO_STYLE = {
  filter: "brightness(0) invert(1)",
  width: "clamp(150px, 17vw, 210px)",
};

const onPurple = (percent: number) =>
  `color-mix(in srgb, var(--brand-white) ${percent}%, transparent)`;

// login.html sets this gutter as a 40px margin on <body>; the padding is inside
// the 100vh here so the card lands on the same 100vh - 80px as it does there.
const Screen = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
  min-height: 100vh;
  padding: 40px;
  background-color: var(--brand-gray-50);

  @media (max-width: 900px) {
    padding: 0;
  }
`;

const Card = styled.div`
  min-height: calc(100vh - 80px);
  border: 1px solid var(--brand-gray-200);
  border-radius: 20px;
  background-color: var(--brand-white);
  box-shadow: 0 1px 2px color-mix(in srgb, var(--brand-black) 4%, transparent),
    0 18px 48px color-mix(in srgb, var(--brand-black) 6%, transparent);

  @media (max-width: 900px) {
    min-height: 100vh;
    border: 0;
    border-radius: 0;
  }
`;

export interface IAuthShellBrand {
  eyebrow: string;
  headline: string;
  text: string;
}

export interface IAuthShellProps {
  brand: IAuthShellBrand;
  icon: ReactNode;
  eyebrow: string;
  title: string;
  subtitle: ReactNode;
  children: ReactNode;
}

export function AuthShell({
  brand,
  icon,
  eyebrow,
  title,
  subtitle,
  children,
}: IAuthShellProps): JSX.Element {
  return (
    // Auth routes render outside AppLayout, so this owns the whole screen.
    <Screen>
      <Card className="flex flex-column lg:flex-row w-full overflow-hidden">
        {/* Dropped rather than stacked on a phone: above the code field, the only
            thing the page is actually for, it would fill the first screen. */}
        <aside
          className="hidden lg:flex flex-column gap-5"
          style={{
            // Half the card, matching login.html's two equal grid columns.
            // Only reachable while the panel is shown, i.e. from lg up.
            flex: "0 0 50%",
            padding: "clamp(32px, 4vw, 64px)",
            backgroundColor: "var(--brand-purple-700)",
            color: "var(--brand-white)",
          }}
        >
          <img
            alt="Clear Health"
            src="/logo_new.svg"
            style={WHITE_LOGO_STYLE}
          />

          <div className="flex flex-1 flex-column justify-content-center gap-4">
            <div>
              <div
                className={EYEBROW_CLASS}
                style={{ ...EYEBROW_STYLE, color: onPurple(72) }}
              >
                {brand.eyebrow}
              </div>
              <h1
                className="mt-3 font-bold"
                style={{
                  color: "var(--brand-white)",
                  fontSize: "2.75rem",
                  lineHeight: 1.12,
                }}
              >
                {brand.headline}
              </h1>
            </div>

            <p
              className="max-w-26rem line-height-3"
              style={{ color: onPurple(82) }}
            >
              {brand.text}
            </p>
          </div>

          <div
            className="flex align-items-center gap-2 pt-4 border-top-1 text-sm"
            style={{ borderColor: onPurple(22), color: onPurple(75) }}
          >
            <i className="pi pi-shield" />
            Secure workspace access for authorized staff
          </div>
        </aside>

        <section className="flex flex-1 flex-column justify-content-center p-4 lg:p-6">
          <div className="w-full mx-auto" style={{ maxWidth: "420px" }}>
            {/* The brand panel is gone on small screens, so the mark comes back
                here — people land on this page from an email and it has to say
                whose app it is. */}
            <img
              alt="Clear Health"
              className="w-8rem mb-5 lg:hidden"
              src="/logo_new.svg"
            />

            <div
              className="flex align-items-center justify-content-center mb-5 border-round-xl"
              style={{
                width: "3.25rem",
                height: "3.25rem",
                backgroundColor:
                  "color-mix(in srgb, var(--brand-purple-700) 12%, var(--brand-white))",
                color: "var(--brand-purple-700)",
              }}
            >
              {icon}
            </div>

            <div
              className={EYEBROW_CLASS}
              style={{ ...EYEBROW_STYLE, color: "var(--brand-purple-700)" }}
            >
              {eyebrow}
            </div>
            <h1
              className="mt-2 font-bold"
              style={{
                color: "var(--brand-black)",
                fontSize: "2rem",
                lineHeight: 1.2,
              }}
            >
              {title}
            </h1>
            <p
              className="mt-2 line-height-3"
              style={{ color: "var(--brand-gray-600)" }}
            >
              {subtitle}
            </p>

            <div className="mt-5">{children}</div>
          </div>
        </section>
      </Card>
    </Screen>
  );
}
